// What's new in Voolist: the page at /changelog, the "What's new" button in the top bar and the "New in
// Voolist" window on the dashboard all read this file. It is written once a month by the monthly update
// routine (.claude/skills/monthly-update/SKILL.md), and a big launch can be added the day it goes live.
// README.md in this folder explains every field; changelog-data.test.js checks them.

// Work already under way, shown at the top of the page without dates. An item leaves this list the day it
// ships and appears in its month below.
export const WORKING_ON = [
  {
    title: 'Voolist mobile apps',
    description: 'List and manage your inventory from your phone, wherever you are.',
    status: 'now',
  },
  {
    title: 'Sale detection for Grailed, Whatnot, OfferUp and Vestiaire Collective',
    description: 'So a sale on one of them takes the item off your other marketplaces on its own.',
    status: 'now',
  },
];

// Newest month first. Inside a month, headliners and updates run from the most valuable to sellers down.
export const CHANGELOG = [
  {
    month: '2026-10',
    title: 'Four New Places to Sell',
    summary:
      'Four new places to sell, right from the listings you already have: Whatnot, Grailed, Vestiaire Collective and OfferUp. Depop sales are now detected for every seller, and Poshmark sales come through faster.',
    headliners: [
      {
        id: 'whatnot',
        date: '2026-10-26',
        kind: 'marketplace',
        platform: 'whatnot',
        beta: true,
        title: 'Sell on Whatnot',
        description:
          "Reach Whatnot's shoppers: list your items on Whatnot's Marketplace as Buy It Now listings, straight from Voolist.",
        action: { label: 'Connect Whatnot', path: '/stores' },
        helpArticleId: 'connect-whatnot',
      },
      {
        id: 'grailed',
        date: '2026-10-24',
        kind: 'marketplace',
        platform: 'grailed',
        beta: true,
        title: 'Sell on Grailed',
        description:
          'Bring your menswear and streetwear to Grailed: list, edit, import and relist there from Voolist, just like on your other marketplaces.',
        action: { label: 'Connect Grailed', path: '/stores' },
        helpArticleId: 'connect-grailed',
      },
      {
        id: 'vestiaire-collective',
        date: '2026-10-31',
        kind: 'marketplace',
        platform: 'vestiaire',
        beta: true,
        title: 'Sell on Vestiaire Collective',
        description:
          'Reach buyers of designer and luxury fashion: list on Vestiaire Collective straight from Voolist. Vestiaire reviews each new listing before it goes live.',
        action: { label: 'Connect Vestiaire Collective', path: '/stores' },
        helpArticleId: 'connect-vestiaire',
      },
      {
        id: 'offerup',
        date: '2026-10-26',
        kind: 'marketplace',
        platform: 'offerup',
        beta: true,
        title: 'Sell on OfferUp',
        description:
          'Sell to buyers near you: post to OfferUp straight from Voolist, and buyers pick up in person. For sellers in the US.',
        action: { label: 'Connect OfferUp', path: '/stores' },
        helpArticleId: 'connect-offerup',
      },
    ],
    updates: [
      { id: 'depop-sales-for-everyone', date: '2026-10-26', area: 'Depop', kind: 'new', text: 'Sales are now detected for every seller: when an item sells on Depop, its stock drops everywhere else, and when it sells somewhere else, it comes off Depop automatically. Detection runs from Chrome, so it works while your computer is on and Chrome is open.' },
      { id: 'poshmark-sales-every-10-minutes', date: '2026-10-21', area: 'Poshmark', text: 'Sales are detected much faster, about every 10 minutes, and items you have more than one of keep the right quantity on Poshmark.' },
      { id: 'edit-from-your-phone', date: '2026-10-23', area: 'Poshmark & Depop', kind: 'new', text: 'Edit these listings from your phone. Your changes are saved right away and sent to Poshmark or Depop the next time Chrome is open on your computer.' },
      { id: 'listings-filters', date: '2026-10-16', area: 'Listings', kind: 'new', text: 'Find listings faster with new filters for stock level, status, marketplace and label, several at once. Archived listings now have search and labels too.' },
      { id: 'store-categories-and-fields', date: '2026-10-19', area: 'WooCommerce & BigCommerce', kind: 'new', text: "Choose your store's own categories and add attributes or custom fields. Tags, brand, barcode and cost are now sent to your store too." },
      { id: 'etsy-shop-sections', date: '2026-10-05', area: 'Etsy', kind: 'new', text: 'Organise your shop: choose a shop section for each listing, right in the listing form.' },
      { id: 'relist-pick-accounts', date: '2026-10-15', area: 'Delist & Relist', text: 'Choose which accounts to relist on, and sold-out items are skipped automatically.' },
    ],
  },
  {
    month: '2026-09',
    title: 'Bulk Delist and Depop Shipping Your Way',
    summary:
      'Take listings down in bulk, stop hunting for the right Poshmark and Depop size, and ship Depop orders your way.',
    headliners: [
      {
        id: 'bulk-delist',
        date: '2026-09-14',
        kind: 'new',
        icon: 'solar:archive-down-minimlistic-bold',
        title: 'Bulk Delist',
        description:
          'Take up to 100 listings off your marketplaces in one go, perfect for a holiday break or a stock clear-out. Everything stays in Voolist, ready to relist in a few clicks.',
        action: { label: 'Open My Listings', path: '/listing' },
        helpArticleId: 'delist',
      },
      {
        id: 'sizes-follow-category',
        date: '2026-09-30',
        kind: 'improved',
        icon: 'solar:hanger-2-bold',
        title: 'Sizes That Follow Your Category',
        description:
          'No more hunting for the right size: Poshmark and Depop sizes now follow the category you choose, in the listing form and in bulk.',
      },
      {
        id: 'depop-shipping-your-way',
        date: '2026-09-25',
        kind: 'new',
        platform: 'depop',
        title: 'Depop Shipping Your Way',
        description:
          "Choose how each Depop item ships (Automatic, Depop's own labels or your own price), set one postage price for every listing with Use for all, and offer worldwide shipping at its own price.",
        helpArticleId: 'depop-listing-options',
      },
    ],
    updates: [
      { id: 'archive-tidies-listings', date: '2026-09-02', area: 'Archive', text: 'Archive now just tidies your Listings page and leaves your marketplace listings live. To take listings down, use Bulk Delist.' },
      { id: 'reconnect-needed-card', date: '2026-09-15', area: 'Marketplaces', text: 'If a marketplace connection expires, Voolist now tells you right away, so you can reconnect and keep everything in sync.' },
      { id: 'ebay-cross-account-details', date: '2026-09-14', area: 'eBay', text: 'Cross-listing from one eBay account to another now fills in the category, condition and item specifics for you.' },
      { id: 'mark-sold-exact-shop', date: '2026-09-03', area: 'Mark as Sold', text: 'Have two shops on one marketplace? You can now pick the exact shop an item sold in.' },
    ],
  },
  {
    month: '2026-08',
    title: 'Easier Bulk Tools and Smarter Imports',
    summary:
      'Cross-list, edit and merge in bulk with fewer steps, let imports bring new listings straight in, and see which of your Poshmark and Depop listings are live.',
    headliners: [
      {
        id: 'bulk-cross-listing-on-its-own-page',
        kind: 'improved',
        icon: 'solar:share-bold',
        title: 'Bulk Cross-List, Step by Step',
        description:
          'Bulk Cross-List opens on its own page and guides you one step at a time. Fill in missing details as you go, give every item the same category or detail in one click, and keep using Voolist while it works.',
      },
      {
        id: 'bulk-edit-with-a-live-preview',
        kind: 'improved',
        icon: 'solar:pen-bold',
        title: 'Bulk Edit with a Live Preview',
        description:
          'Edit many listings in one table: titles, prices, quantities, SKUs and more. Change a whole batch in one go, like raising prices by 10% or rounding to .99, and see every new value before you save.',
      },
      {
        id: 'merge-duplicates-rebuilt',
        kind: 'improved',
        icon: 'solar:link-bold',
        title: 'Find and Merge Duplicates',
        description:
          'Voolist finds duplicates across your whole inventory and shows why they match. Merge them into the listing you keep: it gets every photo and fills in any details it was missing.',
      },
    ],
    updates: [
      { id: 'imports-that-only-ask-about-look', area: 'Imports', text: 'When you import from a marketplace, new listings come straight in. Voolist only asks you about items that look like one you already have.' },
      { id: 'poshmark-and-depop-publish-status-your', area: 'Poshmark & Depop', kind: 'new', text: 'My Listings shows which Poshmark and Depop listings are live, which are on their way, and what to do when one needs a look.' },
      { id: 'poshmark-and-depop-poshmark-canada-accounts', area: 'Poshmark', text: 'Poshmark Canada listings are priced in Canadian dollars when you publish or edit them.' },
    ],
  },
  {
    month: '2026-07',
    title: 'Easier Reconnecting',
    summary:
      'Know right away when a marketplace account needs reconnecting, and pick up exactly where you left off once you do.',
    headliners: [
      {
        id: 'know-when-an-account-needs-reconnecting',
        kind: 'improved',
        icon: 'solar:plug-circle-bold',
        title: 'Know When an Account Needs Reconnecting',
        description:
          'When a marketplace account needs reconnecting, Voolist tells you: the listing form shows which account it is, with a link to reconnect, and its listings are flagged in My Listings.',
      },
      {
        id: 'reconnecting-keeps-everything-in-place',
        kind: 'improved',
        icon: 'solar:refresh-circle-bold',
        title: 'Reconnecting Keeps Everything in Place',
        description:
          'When your eBay account needs reconnecting, nothing is lost. Reconnect, and your listings carry on as before.',
      },
    ],
    updates: [],
  },
  {
    month: '2026-06',
    title: 'Scheduled Listings and eBay Auctions',
    summary:
      'Schedule listings to go live later, sell on eBay by auction, edit photos as you list, and set up your eBay policies without leaving Voolist.',
    headliners: [
      {
        id: 'schedule-listings-for-later',
        kind: 'new',
        icon: 'solar:clock-circle-bold',
        title: 'Schedule Listings for Later',
        description:
          'Click Schedule in the listing form, pick a date and time, and Voolist publishes your listing for you. It uses the timezone set in your account settings.',
      },
      {
        id: 'in-form-photo-editor',
        kind: 'new',
        icon: 'solar:gallery-edit-bold',
        title: 'Edit Photos in the Listing Form',
        description:
          'Crop, rotate and touch up your photos as you list, without opening another app.',
      },
      {
        id: 'ebay-auctions-and-motors-fitment',
        kind: 'new',
        icon: 'solar:tag-price-bold',
        title: 'eBay Auctions and Auto Parts',
        description:
          'Sell on eBay by auction and choose how long it runs. Selling auto parts? Add the vehicles they fit right in the listing form.',
      },
    ],
    updates: [
      { id: 'reliable-cross-platform-sync-a-major', area: 'Sales', kind: 'new', text: 'When the last one sells, Voolist takes the listing off eBay, Shopify and Etsy for you.' },
      { id: 'iphone-photo-heic-uploads-upload-photos', area: 'Photos', kind: 'new', text: 'Upload photos straight from your iPhone, and Voolist makes them work on every marketplace.' },
      { id: 'shipping-profiles-and-policy-management-create', area: 'Shipping Profiles', kind: 'new', text: 'Create and edit your eBay shipping, return and payment policies, and your Etsy return policies, without leaving Voolist.' },
      { id: 'smarter-description-templates-drop-tokens-like', area: 'Templates', text: "Add a listing's title, price or SKU to a description template, and Voolist fills in the real details for each listing. You can also use your own HTML design." },
      { id: 'marketplaces-pull-a-listing-s-latest', area: 'eBay & Etsy', text: 'Changed a listing on eBay or Etsy? Bring its latest version into Voolist with one click in the listing form.' },
      { id: 'internal-notes-keep-private-notes-on', area: 'Listing form', kind: 'new', text: 'Keep private notes on any listing, like sourcing info, measurements or reminders. Only you can see them.' },
      { id: 'upc-barcode-field-add-a-product', area: 'Listing form', kind: 'new', text: "Add a listing's barcode (UPC), and Voolist sends it to eBay, Shopify and BigCommerce for you." },
    ],
  },
  {
    month: '2026-05',
    title: 'Poshmark Sales and Multiple Accounts',
    summary:
      'When something sells on Poshmark, Voolist takes it off your other marketplaces. You can also connect more than one account per marketplace and give each marketplace its own title, description and price.',
    headliners: [
      {
        id: 'poshmark-sales-detection',
        kind: 'new',
        icon: 'solar:hand-money-bold',
        title: 'Poshmark Sales Detection',
        description:
          'When something sells on Poshmark, Voolist spots the sale and takes the item off your other marketplaces for you.',
      },
      {
        id: 'multiple-accounts-per-platform',
        kind: 'new',
        icon: 'solar:users-group-rounded-bold',
        title: 'Multiple Accounts per Marketplace',
        description:
          'Connect more than one account on eBay, Etsy, Shopify, WooCommerce or BigCommerce. Give each one its own label and settings, and pick exactly which accounts to publish or cross-list to.',
      },
      {
        id: 'per-platform-title-description-and-price',
        kind: 'new',
        icon: 'solar:layers-minimalistic-bold',
        title: 'A Title, Description and Price for Each Marketplace',
        description:
          'Give each marketplace its own title, description and price, so every listing fits how buyers shop there.',
      },
    ],
    updates: [
      { id: 'redesigned-listing-form-drafts-save-automatically', area: 'Listing form', text: 'The listing form keeps your work as you type, so if you close the tab by mistake, click Restore to pick up where you left off.' },
      { id: 'best-offer-for-ebay-enable-best', area: 'eBay', kind: 'new', text: 'Turn on Best Offer for your eBay listings, and set the prices at which offers are accepted or declined automatically.' },
      { id: 'description-templates-build-reusable-description-templates', area: 'Templates', kind: 'new', text: 'Save descriptions you use often as templates, and add one to any listing with Use Template.' },
      { id: 'listing-labels-tag-listings-with-custom', area: 'Listings', kind: 'new', text: 'Group your listings with your own labels and filter by them.' },
      { id: 'marketplaces-etsy-image-upload-limit-raised', area: 'Etsy', text: 'Add up to 20 photos to an Etsy listing.' },
      { id: 'marketplaces-etsy-auto-renewal-option-in', area: 'Etsy', text: 'Choose whether each Etsy listing renews automatically or manually.' },
      { id: 'inkfrog-importer-move-your-listings-from', area: 'Imports', kind: 'new', text: 'Moving from inkFrog? Upload your inkFrog export, and Voolist brings your listings in without duplicating ones you already have.' },
    ],
  },
  {
    month: '2026-04',
    title: 'Cross-List Everywhere',
    summary:
      'Cross-list to Depop and Poshmark in bulk, with the category, size, color and brand filled in for you, and archive many listings at once.',
    headliners: [
      {
        id: 'bulk-cross-list-to-depop-and-poshmark',
        kind: 'new',
        icon: 'solar:layers-minimalistic-bold',
        title: 'Bulk Cross-List to Depop & Poshmark',
        description:
          'Select your listings and cross-list them to Depop and Poshmark in one go. Voolist fills in the category, size, color and brand for each marketplace, so there is little to set up by hand.',
      },
      {
        id: 'bulk-archive',
        kind: 'new',
        icon: 'solar:archive-bold',
        title: 'Bulk Archive',
        description:
          'Archive many listings at once instead of one by one.',
      },
    ],
    updates: [
      { id: 'fixed-image-quality-loss-when-delisting', area: 'Photos', text: 'Your photos stay sharp when you cross-list from eBay or delist and relist.' },
      { id: 'cross-listing-auto-connect-for-poshmark', area: 'Poshmark & Depop', text: 'Connect Poshmark and Depop in one click on the Marketplaces page.' },
      { id: 'smarter-ai-listing-generator-the-ai', area: 'AI Listing Generator', text: "It also picks the category on each of your marketplaces, so you don't have to." },
    ],
  },
  {
    month: '2026-03',
    title: 'Smarter Cross-Listing and Help Guides',
    summary:
      'Catch missing details before you cross-list, and find answers in step-by-step help guides inside Voolist.',
    headliners: [
      {
        id: 'platform-validation-for-bulk-cross-listing',
        kind: 'new',
        icon: 'solar:checklist-bold',
        title: 'Catch Problems Before You Cross-List',
        description:
          "Before you cross-list, Voolist checks each marketplace's title length and required details, so you can fix them before anything is posted.",
      },
      {
        id: 'in-app-knowledge-base',
        kind: 'new',
        icon: 'solar:book-bold',
        title: 'Help Guides Inside Voolist',
        description:
          'Step-by-step guides with screenshots and videos on importing, cross-listing and more. Open them any time from Help, at the top of every page.',
      },
      {
        id: 'shopify-auto-publish',
        kind: 'new',
        icon: 'solar:shop-bold',
        title: 'Shopify Listings Go Live Automatically',
        description:
          'Listings you cross-list to Shopify appear on your Online Store, with nothing to publish by hand.',
      },
    ],
    updates: [],
  },
  {
    month: '2026-02',
    title: 'Poshmark, Depop and Spreadsheet Import',
    summary:
      'Sell on Poshmark and Depop from Voolist, and create many listings at once from a spreadsheet.',
    headliners: [
      {
        id: 'poshmark',
        kind: 'marketplace',
        platform: 'poshmark',
        title: 'Sell on Poshmark',
        description:
          'Connect Poshmark with the free Voolist Chrome extension, then import, publish, edit and delist your Poshmark listings right from Voolist.',
      },
      {
        id: 'depop',
        kind: 'marketplace',
        platform: 'depop',
        title: 'Sell on Depop',
        description:
          'Connect Depop with the free Voolist Chrome extension, then import, publish, edit and delist your Depop listings right from Voolist.',
      },
      {
        id: 'csv-import',
        kind: 'new',
        icon: 'solar:document-add-bold',
        title: 'Create Listings from a Spreadsheet',
        description:
          'Create hundreds of listings at once from a spreadsheet. Click Import from CSV, download the template, fill it in, and Voolist walks you through the rest.',
      },
    ],
    updates: [
      { id: 'ebay-auto-offers-automatically-send-offers', area: 'eBay', kind: 'new', text: 'Auto Offer to Watchers sends a discounted offer to buyers watching your eBay listings, with the discount and message you choose.' },
      { id: 'brand-and-color-fields-every-listing', area: 'Listing form', kind: 'new', text: 'Search for a brand and pick a primary and secondary color on any listing. They carry over when you cross-list to Poshmark and Depop.' },
    ],
  },
  {
    month: '2026-01',
    title: 'Bulk Edit, Cross-List and Merge',
    summary:
      'Change many listings at once, cross-list many to eBay and Etsy, and merge duplicate listings into one.',
    headliners: [
      {
        id: 'bulk-edit-multiple-listings',
        kind: 'new',
        icon: 'solar:pen-new-square-bold',
        title: 'Edit Many Listings at Once',
        description:
          'Select your listings and change their prices, quantities or titles all at once, instead of opening them one by one.',
      },
      {
        id: 'bulk-cross-listing-for-ebay-and-etsy',
        kind: 'new',
        icon: 'solar:layers-minimalistic-bold',
        title: 'Bulk Cross-List to eBay & Etsy',
        description:
          'Cross-list many listings to eBay and Etsy at once.',
      },
      {
        id: 'merge-listings-and-duplicate-detection',
        kind: 'new',
        icon: 'solar:copy-bold',
        title: 'Merge Duplicate Listings',
        description:
          "Combine duplicate listings into one with Merge. Voolist also spots likely duplicates when you import, so you don't end up with two of the same item.",
      },
    ],
    updates: [
      { id: 'real-time-progress-tracking-watch-your', area: 'Bulk Actions', kind: 'new', text: 'Watch your bulk edits, cross-listing and Delist & Relist progress while you keep working.' },
    ],
  },
  {
    month: '2025-12',
    title: 'Mark as Sold and Delist & Relist',
    summary:
      'Mark items as sold when you sell them in person or elsewhere, give listings a fresh start with Delist & Relist, and keep your stock correct when something sells on Etsy.',
    headliners: [
      {
        id: 'mark-listings-as-sold',
        kind: 'new',
        icon: 'solar:tag-price-bold',
        title: 'Mark Listings as Sold',
        description:
          "Sold something in person or somewhere Voolist doesn't track? Mark it as sold, enter how many sold, and your stock stays correct on every marketplace.",
      },
      {
        id: 'delist-and-relist-in-one-click',
        kind: 'new',
        icon: 'solar:refresh-circle-bold',
        title: 'Delist & Relist in One Click',
        description:
          'Give listings a fresh start: Delist & Relist takes them down and puts them straight back up as new listings, one at a time or in bulk. Views and watchers start again from zero.',
      },
      {
        id: 'etsy-real-time-sync',
        kind: 'new',
        icon: 'solar:shop-bold',
        title: 'Etsy Sales Update Your Other Marketplaces',
        description:
          "When something sells on Etsy, its stock updates on your other marketplaces right away, so you don't sell more than you have.",
      },
    ],
    updates: [
      { id: 'unmanage-listings-want-to-stop-managing', area: 'Remove from Voolist', kind: 'new', text: 'Take a listing out of Voolist and leave it live on your marketplaces.' },
    ],
  },
  {
    month: '2025-11',
    title: 'Cross-List Many Listings at Once',
    summary:
      'Cross-list many listings at once, and use your own units and currency for weights, measurements and prices.',
    headliners: [
      {
        id: 'bulk-cross-listing-engine',
        kind: 'new',
        icon: 'solar:layers-minimalistic-bold',
        title: 'Bulk Cross-Listing',
        description:
          'Select your listings and cross-list them all in one go. Voolist checks each listing first, so you can fix problems before anything is published.',
      },
      {
        id: 'measurement-and-currency-support',
        kind: 'new',
        icon: 'solar:ruler-pen-bold',
        title: 'Your Units and Currency',
        description:
          'Enter weights, measurements and prices your way: kilograms or pounds, centimeters or inches, and currencies like USD, GBP, EUR, CAD and AUD. Choose yours on the Store Preference page.',
      },
    ],
    updates: [],
  },
  {
    month: '2025-10',
    title: 'Import Everything and List Faster',
    summary:
      'Import all your listings in one go, start new listings from ones you already have, and have your eBay policies filled in for you.',
    headliners: [
      {
        id: 'lightning-fast-import-system',
        kind: 'new',
        icon: 'solar:rocket-bold',
        title: 'Import All Your Listings',
        description:
          "Bring in every listing from your connected marketplaces, whether you have 10 or thousands. Keep working while they come in, and Voolist lets you know when it's done.",
      },
      {
        id: 'use-any-listing-as-a-template',
        kind: 'new',
        icon: 'solar:copy-bold',
        title: 'Use Any Listing as a Template',
        description:
          'Selling something similar? Click Use as Template on any listing, change the title or photos, and the rest (description, policies, price) is already filled in.',
      },
      {
        id: 'ebay-policy-templates-and-smart-defaults',
        kind: 'new',
        icon: 'solar:bookmark-bold',
        title: 'Set Your eBay Policies Once',
        description:
          'Save your eBay shipping, return and payment policies once, star the ones you use most, and every new listing starts with them filled in.',
      },
    ],
    updates: [
      { id: 'ebay-subtitle-field-support-for-better', area: 'eBay', text: 'Add a subtitle to your eBay listings.' },
    ],
  },
  {
    month: '2025-09',
    title: 'AI Listings, Archive and Live Chat',
    summary:
      'Let AI write your titles and descriptions from your photos, archive listings you can restore any time, and chat with our support team inside Voolist.',
    headliners: [
      {
        id: 'ai-powered-listing-generator',
        kind: 'new',
        icon: 'solar:magic-stick-3-bold',
        title: 'AI Listing Generator',
        description:
          'Add your photos, click Generate Listing with AI, and get a title and description written for you.',
      },
      {
        id: 'archive-and-restore-listings',
        kind: 'new',
        icon: 'solar:archive-bold',
        title: 'Archive & Restore Listings',
        description:
          "Move listings you don't need right now to Archived Listings, and restore them any time.",
      },
      {
        id: 'live-chat-support',
        kind: 'new',
        icon: 'solar:chat-round-bold',
        title: 'Chat with Our Team',
        description:
          'Questions? Click Chat with us in the side menu to talk to our support team without leaving Voolist.',
      },
    ],
    updates: [
      { id: 'drag-and-drop-image-reordering-easily', area: 'Photos', kind: 'new', text: 'Drag your photos into the order you want, with your best one first.' },
      { id: 'smart-category-suggestions-the-platform-now', area: 'Listing form', kind: 'new', text: "Categories you've used recently show up first, so you don't have to search for them every time." },
      { id: 'enhanced-category-search-finding-the-right', area: 'Listing form', kind: 'new', text: 'Find any category by typing a few letters.' },
      { id: 'ebay-support-for-more-ebay-marketplaces', area: 'eBay', text: 'Connect eBay accounts from the UK, Canada, Australia, Germany, France, Italy, Spain and more.' },
    ],
  },
];
